import type { DeviceSpecDevice, DeviceSpecTag } from '../../types'
import { DATA_TYPES, channelDef } from './catalog'
import { focasCheck } from './focas'
import type { ChannelDef } from './catalog'

// 디바이스 명세 편집 규칙 — 초안은 형식이 틀려도 저장한다. 여기 검증은 "Edge 에 넣기 전 확인할 것" 표시용이다.

export const newKey = () => (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/-/g, '')

export const emptyTag = (): DeviceSpecTag => ({
  key: newKey(), name: '', address: '', size: '1', dataType: 'Word', arithmetic: '', description: '', alias: '',
})

export function defaultChannel(type: string): Record<string, string> {
  const def = channelDef(type)
  return Object.fromEntries((def?.fields ?? []).map(f => [f.key, f.defaultValue]))
}

export const emptyDevice = (type = ''): DeviceSpecDevice => ({
  key: newKey(), category: '', maker: '', model: '', code: '', name: '', description: '',
  channelType: type, channel: defaultChannel(type), memo: '', interfaceId: '', tags: [],
})

// 프로토콜을 바꾸면 같은 이름의 필드 값은 이어받고, 새 유형에만 있는 필드는 기본값으로
export function changeChannelType(device: DeviceSpecDevice, type: string): DeviceSpecDevice {
  const next = defaultChannel(type)
  for (const key of Object.keys(next)) {
    if (device.channel[key]) next[key] = device.channel[key]
  }
  return { ...device, channelType: type, channel: next }
}

// ── 접속 정보 — 시트에서는 한 칸(IP:포트 / URI / COM 포트 …)으로 보고 고친다 ──

export function endpointText(device: DeviceSpecDevice): string {
  const def = channelDef(device.channelType)
  const c = device.channel
  switch (def?.endpoint) {
    case 'ip': return c.ip ? (c.port ? `${c.ip}:${c.port}` : c.ip) : ''
    case 'uri': return c.serverUri ?? ''
    case 'serial': return c.comPort ? (c.baudRate ? `${c.comPort} @${c.baudRate}` : c.comPort) : ''
    case 'path': return c.path ?? ''
    case 'folder': return c.folderKey ?? ''
    default: return ''
  }
}

// "192.168.30.101:4001", "192.168.30.102/4001"(엑셀 오타), "192.168.30.152" 모두 받는다
export function parseEndpoint(def: ChannelDef | undefined, raw: string): Record<string, string> {
  const s = raw.trim()
  switch (def?.endpoint) {
    case 'ip': {
      const m = /^(?:[a-z]+:\/\/)?([^:/\s]+)(?:\s*[:/]\s*(\d+))?\/?$/i.exec(s)
      if (!m) return { ip: s }
      return m[2] ? { ip: m[1], port: m[2] } : { ip: m[1] }
    }
    case 'uri': return { serverUri: s }
    case 'serial': {
      const m = /^(\S+?)(?:\s*@\s*(\d+))?$/.exec(s)
      return m ? (m[2] ? { comPort: m[1], baudRate: m[2] } : { comPort: m[1] }) : { comPort: s }
    }
    case 'path': return { path: s }
    case 'folder': return { folderKey: s }
    default: return {}
  }
}

// ── 태그 주소 — LiveEdge 형식으로 맞춘다 ──

// Modbus: "국번-기능코드x주소" (예: 1-3x0). 엑셀의 "1, 3x0" / "1 3x0" / 국번 열 + "3x0" 도 받는다
export function normalizeAddress(def: ChannelDef | undefined, raw: string, slave?: string): string {
  const s = raw.trim()
  if (def?.address !== 'modbus') return s
  const full = /^(\d+)\s*[,\s-]\s*(\d+)\s*x\s*(\d+)$/i.exec(s)
  if (full) return `${full[1]}-${full[2]}x${full[3]}`
  const short = /^(\d+)\s*x\s*(\d+)$/i.exec(s)
  if (short && slave?.trim()) return `${slave.trim()}-${short[1]}x${short[2]}`
  return s
}

const DATA_TYPE_ALIASES: Record<string, string> = {
  '비트': 'Bit', '워드': 'Word', 'u워드': 'UWord', '교차 ascii': 'CrossAscii', 'date and time': 'DateTime', 'time(sec)': 'TimeSec',
  'int16': 'Word', 'short': 'Word', 'uint16': 'UWord', 'ushort': 'UWord', 'float32': 'Float', 'bool': 'Bit', 'boolean': 'Bit',
}

export function normalizeDataType(raw: string): string {
  const s = raw.trim()
  if (!s) return 'Word'
  const lower = s.toLowerCase()
  return DATA_TYPES.find(t => t.toLowerCase() === lower) ?? DATA_TYPE_ALIASES[lower] ?? s
}

// ── 검증 ──

export type TagField = 'address' | 'size' | 'dataType'
export type DeviceField = 'code' | 'name' | 'channelType' | 'endpoint'

export interface SpecIssues {
  device: Record<string, Partial<Record<DeviceField, string>>>
  tag: Record<string, Partial<Record<TagField, string>>>
  count: number
}

const IPV4 = /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/

export function validateSpec(devices: DeviceSpecDevice[]): SpecIssues {
  const issues: SpecIssues = { device: {}, tag: {}, count: 0 }
  const codeCount = new Map<string, number>()
  devices.forEach(d => codeCount.set(d.code.trim(), (codeCount.get(d.code.trim()) ?? 0) + 1))

  for (const d of devices) {
    const e: Partial<Record<DeviceField, string>> = {}
    const def = channelDef(d.channelType)
    if (!d.code.trim()) e.code = '필수'
    else if ((codeCount.get(d.code.trim()) ?? 0) > 1) e.code = '중복'
    if (!d.name.trim()) e.name = '필수'
    if (!def) e.channelType = '선택'
    else if (def.endpoint === 'ip' && !IPV4.test(d.channel.ip ?? '')) e.endpoint = 'IP 확인'
    else if (def.endpoint === 'uri' && !(d.channel.serverUri ?? '').trim()) e.endpoint = '필수'
    else if (def.type === 'Custom' && !(d.channel.filePath ?? '').trim()) e.endpoint = '모듈 경로 필수'
    if (Object.keys(e).length > 0) { issues.device[d.key] = e; issues.count += Object.keys(e).length }

    const addressCount = new Map<string, number>()
    d.tags.forEach(t => addressCount.set(t.address.trim(), (addressCount.get(t.address.trim()) ?? 0) + 1))
    for (const t of d.tags) {
      const te: Partial<Record<TagField, string>> = {}
      if (!t.address.trim()) te.address = '필수'
      else if ((addressCount.get(t.address.trim()) ?? 0) > 1) te.address = '중복'
      else if (def?.address === 'modbus' && !/^\d+-\d+x\d+$/.test(t.address.trim())) te.address = '형식: 1-3x0'
      else if (def?.address === 'focas' && focasCheck(t.address)) te.address = focasCheck(t.address)!
      const size = Number(t.size)
      if (!Number.isInteger(size) || size < 1) te.size = '1 이상'
      if (!DATA_TYPES.includes(t.dataType)) te.dataType = '알 수 없음'
      if (Object.keys(te).length > 0) { issues.tag[t.key] = te; issues.count += Object.keys(te).length }
    }
  }
  return issues
}

// ── 점검 칸 이동 ([다음 문제]) ──

/** 점검에 걸린 칸 하나 — row 가 null 이면 디바이스 칸 (시트·[설정] 서랍), 아니면 그 태그 행 */
export interface IssueSpot { deviceKey: string; deviceIndex: number; row: number | null; field: DeviceField | TagField; message: string }

// 시트 열 순서대로 (디바이스: 이름 → 프로토콜 → 접속 → 코드, 태그: 주소 → 크기 → 타입)
const DEVICE_ORDER: DeviceField[] = ['name', 'channelType', 'endpoint', 'code']
const TAG_ORDER: TagField[] = ['address', 'size', 'dataType']

/** 점검에 걸린 칸 — 디바이스 순서, 디바이스 칸 먼저, 그다음 태그 행 순서 */
export function issueSpots(devices: DeviceSpecDevice[], issues: SpecIssues): IssueSpot[] {
  const out: IssueSpot[] = []
  devices.forEach((d, deviceIndex) => {
    const de = issues.device[d.key]
    if (de) for (const field of DEVICE_ORDER) if (de[field]) out.push({ deviceKey: d.key, deviceIndex, row: null, field, message: de[field]! })
    d.tags.forEach((t, row) => {
      const te = issues.tag[t.key]
      if (te) for (const field of TAG_ORDER) if (te[field]) out.push({ deviceKey: d.key, deviceIndex, row, field, message: te[field]! })
    })
  })
  return out
}

/** 디바이스·태그 한 곳의 점검 칸 수 (편집 모드 탭 표시) */
export const deviceIssueCount = (d: DeviceSpecDevice, issues: SpecIssues) =>
  Object.keys(issues.device[d.key] ?? {}).length + d.tags.reduce((n, t) => n + Object.keys(issues.tag[t.key] ?? {}).length, 0)

const spotOrder = (s: { deviceIndex: number; row: number | null; field: string }) =>
  [s.deviceIndex, s.row ?? -1, s.row == null ? DEVICE_ORDER.indexOf(s.field as DeviceField) : TAG_ORDER.indexOf(s.field as TagField)]

/** 지금 자리(at) 다음 점검 칸 — 끝이면 처음으로 (delta -1 이면 이전) */
export function nextSpot(spots: IssueSpot[], at: { deviceIndex: number; row: number | null; field: string | null } | null, delta: 1 | -1 = 1): IssueSpot | null {
  if (spots.length === 0) return null
  if (!at) return delta === 1 ? spots[0] : spots[spots.length - 1]
  const here = spotOrder({ ...at, field: at.field ?? '' })
  const cmp = (s: IssueSpot) => {
    const o = spotOrder(s)
    for (let i = 0; i < 3; i++) if (o[i] !== here[i]) return o[i] - here[i]
    return 0
  }
  return delta === 1
    ? spots.find(s => cmp(s) > 0) ?? spots[0]
    : [...spots].reverse().find(s => cmp(s) < 0) ?? spots[spots.length - 1]
}
